// src/components/LanguagePicker.tsx — modal with two exclusion-linked scroll-snap wheels.
// Scroll/drag/tap to bring a language to the highlighted center row; selection commits live.
import { useEffect, useRef } from "react";
import type { LanguagePair } from "../core/models";
import { LANGS, PICKER_ORDER } from "../langs";
import { Ico } from "./Icons";

const ITEM_H = 44; // px — must match .wheel-item height and .wheel-pad (2*ITEM_H) in CSS
const SETTLE_MS = 110;

interface Props {
  pair: LanguagePair;
  onChange: (pair: LanguagePair) => void; // live-commit (clears convo, resets listenError)
  onSwap: () => void;
  onClose: () => void;
}

function Wheel({
  codes,
  value,
  onSelect,
}: {
  codes: string[];
  value: string;
  onSelect: (code: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const dragging = useRef(false);
  const idx = Math.max(0, codes.indexOf(value));

  // Center the selected item whenever the list or value changes (but not mid-scroll).
  useEffect(() => {
    const el = ref.current;
    if (!el || dragging.current) return;
    el.scrollTop = idx * ITEM_H;
  }, [idx, codes.length]);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    dragging.current = true;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      dragging.current = false;
      const i = Math.max(0, Math.min(codes.length - 1, Math.round(el.scrollTop / ITEM_H)));
      const code = codes[i];
      if (code && code !== value) onSelect(code);
      else el.scrollTop = i * ITEM_H; // settle exactly onto the snap
    }, SETTLE_MS);
  };

  return (
    <div
      className="wheel"
      ref={ref}
      onScroll={onScroll}
      onKeyDown={(e) => {
        const i = codes.indexOf(value);
        if (e.key === "ArrowDown" && i < codes.length - 1) {
          e.preventDefault();
          onSelect(codes[i + 1]);
        } else if (e.key === "ArrowUp" && i > 0) {
          e.preventDefault();
          onSelect(codes[i - 1]);
        }
      }}
      role="listbox"
      tabIndex={0}
    >
      <div className="wheel-pad" aria-hidden="true" />
      {codes.map((c) => (
        <div
          key={c}
          className={"wheel-item " + (c === value ? "sel" : "")}
          role="option"
          aria-selected={c === value}
          onClick={() => onSelect(c)}
        >
          {LANGS[c] || c}
        </div>
      ))}
      <div className="wheel-pad" aria-hidden="true" />
    </div>
  );
}

export default function LanguagePicker({ pair, onChange, onSwap, onClose }: Props) {
  // Exclusion: each wheel omits the OTHER side's current selection.
  const nativeCodes = PICKER_ORDER.filter((c) => c !== pair.foreign);
  const foreignCodes = PICKER_ORDER.filter((c) => c !== pair.native);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal langpicker"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose languages"
      >
        <div className="modal-head">
          <span className="modal-title">Languages</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <Ico.close />
          </button>
        </div>

        <div className="wheels">
          <div className="wheel-col">
            <div className="wheel-label">You speak</div>
            <div className="wheel-wrap">
              <Wheel
                codes={nativeCodes}
                value={pair.native}
                onSelect={(code) => onChange({ native: code, foreign: pair.foreign })}
              />
              <div className="wheel-highlight" aria-hidden="true" />
            </div>
          </div>

          <button className="wheel-swap" onClick={onSwap} aria-label="Swap languages">
            <Ico.swap />
          </button>

          <div className="wheel-col">
            <div className="wheel-label">They speak</div>
            <div className="wheel-wrap">
              <Wheel
                codes={foreignCodes}
                value={pair.foreign}
                onSelect={(code) => onChange({ native: pair.native, foreign: code })}
              />
              <div className="wheel-highlight" aria-hidden="true" />
            </div>
          </div>
        </div>

        <button className="modal-done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
